// https://www.automasean.blog/react-hash-link/
import HashLinkObserver from 'react-hash-link';
import { Suspense, useEffect, createContext, PropsWithChildren } from 'react';
import 'url-search-params-polyfill';
import { Global } from '@emotion/react';
import * as Sentry from '@sentry/react';
import {
  Redirect,
  Route,
  Switch,
  useRouteMatch,
  useLocation,
  useHistory,
} from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';

import ReferralLanding from './routes/ReferralLanding';
import UpdatedCharter from './routes/UpdatedCharter';

// subscription onboarding
import ContinueOnboarding from './components/onboarding/ContinueOnboarding';

// account
import UpdatePayment from './components/payment/UpdatePayment';
import ForgotPassword from './components/account/ForgotPassword';
import RequestPassReset from './components/account/RequestPassReset';
import ConfirmEmail from './components/account/ConfirmEmail';
import LogOut from './routes/LogOut';
import SubscriptionChurnNotice from './components/payment/SubscriptionChurnNotice';

// misc
import NotFound from './routes/NotFound';
import Private from './routes/Private';
import CancellationMessage from './routes/CancellationMessage';
import BonusIncentive from './components/experiments/BonusIncentive ';

// ui
import Nav from './components/nav/Nav';
import SuspenseFallback from './components/SuspenseFallback';
import * as GroupCallout from './components/onboarding/GroupCallout';

import * as NavConfig from './config/nav';
import Toaster from './components/Toaster';

import ErrorMessage from './components/ErrorMessage';
import GlobalStyles from './style/global';
import usePathHistory from './hooks/usePathHistory';
import useLastNonCheckoutPage from './hooks/useLastNonCheckoutPage';
import { ClaimSpecialOffer, UpdateCurrency } from './actions/user';
import { Step, useOnboardingFlow } from './hooks/useOnboardingFlow';
import useQueryParams from './hooks/useQueryParams';
import SpecialOfferLanding from './routes/SpecialOfferLanding';
import { SetToast } from './actions/toaster';
import AdminModeControls from './components/account/AdminModeControls';
import { isDelinquent } from './lib/subscriptions';
import { lazyDefault } from './lib/lazyNamed';
import { SpecialOffers } from './components/specialOffers/SpecialOffers';
import UpdateTeamPaymentMethod from './components/payment/UpdateTeamPaymentMethod';
import { shouldAutoSetCurrencyFromCountry } from './util/util';
import { guessCurrency } from './util/conversions';
import { State } from './store';
import { selectIsTeamAdmin } from './selectors/team';
import ContentfulBlogPost from './components/blog/ContentfulBlogPost';
import JameoCaseStudy from './routes/Teams/JameoCaseStudy';
import EavoluCaseStudy from './routes/Teams/EavoluCaseStudy';
import BusinessLandingSkeleton from './routes/Teams/BusinessLandingSkeleton';
import useBusinessLandingTransition from './routes/Teams/useBusinessLandingTransition';
import TomorrowsAirBanner from './components/specialOffers/TomorrowsAirBanner';
import DashboardSkeleton from './components/dashboard/DashboardSkeleton';

const ReferralDialogLazy = lazyDefault(
  () => import('./components/ReferralDialog')
);
const ReferralDialog = () => (
  <Suspense fallback={null}>
    <ReferralDialogLazy />
  </Suspense>
);

// interfaces that can be loaded in later
const DonationReceipt = lazyDefault(() => import('./routes/DonationReceipt'));
const ImpactCertificate = lazyDefault(
  () => import('./routes/ImpactCertificate')
);
const PurchasePage = lazyDefault(() => import('./routes/PurchasePage'));
const RetirementPage = lazyDefault(() => import('./routes/RetirementPage'));
const SubscriptionSuccess = lazyDefault(
  () => import('./routes/SubscriptionSuccess')
);
const IntroSurvey = lazyDefault(() => import('./routes/IntroSurvey'));
const AboutOffsets = lazyDefault(() => import('./routes/AboutOffsets'));
const PrivacyPolicy = lazyDefault(() => import('./routes/PrivacyPolicy'));
const ShopifyPrivacyPolicy = lazyDefault(
  () => import('./routes/ShopifyPrivacyPolicy')
);
const ShopifyHelp = lazyDefault(() => import('./routes/ShopifyHelp'));
const ExtensionPrivacyPolicy = lazyDefault(
  () => import('./routes/ExtensionPrivacyPolicy')
);
const TermsOfUse = lazyDefault(() => import('./routes/TermsOfUse'));
const Certification = lazyDefault(() => import('./routes/Certification'));
const AboutUs = lazyDefault(() => import('./routes/AboutUs'));
const AccountSettings = lazyDefault(() => import('./routes/AccountSettings'));
const Actions = lazyDefault(() => import('./routes/Actions'));
const AllBlogPosts = lazyDefault(() => import('./routes/AllBlogPosts'));
const Calculator = lazyDefault(() => import('./routes/Calculator'));
const EmissionsAgenticWizard = lazyDefault(
  () => import('./routes/Emissions/AgenticWizard')
);
const Careers = lazyDefault(() => import('./routes/Careers'));
const Checkout = lazyDefault(() => import('./routes/Checkout'));
const Crypto = lazyDefault(() => import('./routes/Crypto'));
const Dashboard = lazyDefault(() => import('./routes/DashboardV2'));
const TeamDashboardLayout = lazyDefault(
  () => import('./components/dashboard/DashboardLayout')
);
const TeamsDashboard = lazyDefault(
  () => import('./routes/Teams/TeamsDashboard')
);
const Emissions = lazyDefault(() => import('./routes/Dashboard/Emissions'));
// ADR 0014 Phase 4 — universal team-mode IA. Each section is its own page.
const TeamHome = lazyDefault(() => import('./routes/Dashboard/Team/Home'));
const TeamReductions = lazyDefault(
  () => import('./routes/Dashboard/Team/Reductions')
);
const TeamCompensate = lazyDefault(
  () => import('./routes/Dashboard/Team/Compensate')
);
const TeamCommsToolkit = lazyDefault(
  () => import('./routes/Dashboard/Team/CommsToolkit')
);
const TeamApi = lazyDefault(() => import('./routes/Dashboard/Team/Api'));
const TeamEmployees = lazyDefault(
  () => import('./routes/Dashboard/Team/Employees')
);
const TeamEmployeeBenefitSetup = lazyDefault(
  () => import('./routes/Dashboard/Team/EmployeeBenefitSetup')
);
const TeamCertificates = lazyDefault(
  () => import('./routes/Dashboard/Team/Certificates')
);

const DeleteAccountPage = lazyDefault(
  () => import('./routes/DeleteAccountPage')
);
const Emails = lazyDefault(() => import('./routes/Emails'));
const FAQ = lazyDefault(() => import('./routes/FAQ'));
const FB = lazyDefault(() => import('./routes/FB'));
const Flights = lazyDefault(() => import('./routes/Flights'));
const FundraiserDonatePage = lazyDefault(() => import('./routes/Fundraisers/Donate')); // prettier-ignore
const FundraiserPage = lazyDefault(() => import('./routes/Fundraisers/FundraiserPage')); // prettier-ignore
const FundraisersIndex = lazyDefault(
  () => import('./routes/Fundraisers/Index')
);
const FundraisersEdit = lazyDefault(() => import('./routes/Fundraisers/Edit'));
const FundraisersNew = lazyDefault(() => import('./routes/Fundraisers/New'));
const FundraiserSuccess = lazyDefault(
  () => import('./routes/Fundraisers/Success')
);
const Gifts = lazyDefault(() => import('./routes/Gifts'));
const Group = lazyDefault(() => import('./routes/Groups/Group'));
const GroupAccept = lazyDefault(() => import('./routes/Groups/GroupAccept'));
const GroupInvite = lazyDefault(() => import('./routes/Groups/GroupInvite'));
const GroupsLanding = lazyDefault(() => import('./routes/Groups/Landing'));
const JobPost = lazyDefault(() => import('./routes/JobPost'));
const Klima = lazyDefault(() => import('./routes/Klima'));
const Leaderboards = lazyDefault(() => import('./routes/Leaderboards'));
const Login = lazyDefault(() => import('./routes/Login'));
const OffsetAnything = lazyDefault(() => import('./routes/OffsetAnythingV2'));
const OffsetOrders = lazyDefault(() => import('./routes/OffsetOrders'));
const OurApproach = lazyDefault(() => import('./routes/OurApproach'));
const OurProjects = lazyDefault(() => import('./routes/Projects'));
const PayItForward = lazyDefault(() => import('./routes/PayItForward'));
const PayItForwardSuccess = lazyDefault(
  () => import('./routes/PayItForwardSuccess')
);
const PayWhatYouWant = lazyDefault(() => import('./routes/PayWhatYouWant'));
const PortfolioPage = lazyDefault(() => import('./routes/Portfolios'));
const Profile = lazyDefault(() => import('./routes/Profile'));
const ProjectPage = lazyDefault(
  () => import('./components/projects/ProjectPage')
);
const PublicFootprint = lazyDefault(() => import('./routes/PublicFootprint'));
const SignUp = lazyDefault(() => import('./routes/SignUp'));
const ReportAndChoosePlan = lazyDefault(
  () => import('./routes/ReportAndChoosePlan')
);
const Teams = lazyDefault(() => import('./routes/Teams'));
const UpgradeSubscription = lazyDefault(
  () => import('./routes/UpgradeSubscription')
);
const WrenAPI = lazyDefault(() => import('./routes/WrenAPI'));
const ProjectsSummary = lazyDefault(() => import('./routes/ProjectsSummary'));
const TravelLanding = lazyDefault(() => import('./routes/Travel/Landing'));
const ShopifyPublicProfile = lazyDefault(
  () => import('./routes/shopify/PublicProfile')
);
const ShopifyOnboarding = lazyDefault(
  () => import('./routes/shopify/Onboarding')
);
const ShopifyChooseAccount = lazyDefault(
  () => import('./routes/shopify/ChooseAccount')
);
const TeamShopify = lazyDefault(
  () => import('./routes/Dashboard/Team/Shopify')
);
const SwitchToTrailblazer = lazyDefault(
  () => import('./routes/AccountSettings/SwitchToTrailblazer')
);
const HNWLandingPage = lazyDefault(() => import('./routes/HNW'));
const HNWDonate = lazyDefault(() => import('./routes/HNW/Donate'));
const ActionCenter = lazyDefault(
  () => import('./routes/Admin/Inventory/ActionCenter')
);
const BirdseyeLanding = lazyDefault(() => import('./routes/BirdseyeLanding'));
const Donate = lazyDefault(() => import('./routes/DonorPortal/Donate'));
const DonateSuccess = lazyDefault(() => import('./routes/DonorPortal/Success'));
const LinkInBio = lazyDefault(() => import('./routes/LinkInBio'));
const LinkInBioDashboard = lazyDefault(
  () => import('./routes/Admin/LinkInBio/LinkInBioDashboard')
);
const PortfolioCreator = lazyDefault(
  () => import('./routes/Admin/PortfolioCreator')
);
const ProjectCreator = lazyDefault(
  () => import('./routes/Admin/ProjectCreator/ProjectCreator')
);
const ProjectsList = lazyDefault(
  () => import('./routes/Admin/ProjectCreator/ProjectsList')
);
const ProjectDetailCard = lazyDefault(
  () => import('./routes/Admin/ProjectCreator/ProjectDetailCard')
);
const ProjectEditor = lazyDefault(
  () => import('./routes/Admin/ProjectCreator/ProjectEditor')
);
const ManualOffsetCreator = lazyDefault(
  () => import('./routes/Admin/ManualOffsetCreator')
);
const Reservations = lazyDefault(() => import('./routes/Admin/Reservations'));
const TeamCreator = lazyDefault(() => import('./routes/Admin/TeamCreator'));
const AdminTeamsDashboard = lazyDefault(
  () => import('./routes/Admin/Teams/TeamsDashboard')
);
const TeamDetailPage = lazyDefault(
  () => import('./routes/Admin/Teams/TeamDetail')
);
const InventoryDashboard = lazyDefault(
  () => import('./routes/Admin/Inventory/InventoryDashboard')
);
const InventoryHelpPage = lazyDefault(
  () => import('./routes/Admin/Inventory/help/InventoryHelpPage')
);
const ProjectDetail = lazyDefault(
  () => import('./routes/Admin/Inventory/ProjectDetail')
);
const RevenueDashboard = lazyDefault(
  () => import('./routes/Admin/Revenue/RevenueDashboard')
);
const ForecastDashboard = lazyDefault(
  () => import('./routes/Admin/Revenue/ForecastDashboard')
);
const Documents = lazyDefault(
  () => import('./routes/Admin/Inventory/DocumentsPage')
);
const PaymentsPage = lazyDefault(
  () => import('./routes/Admin/Inventory/PaymentsPage')
);
const RedactionQueuePage = lazyDefault(
  () => import('./routes/Admin/Inventory/RedactionQueuePage')
);
const PaymentDetailPage = lazyDefault(
  () => import('./routes/Admin/Inventory/PaymentDetailPage')
);
const IssuancesRetirementsPage = lazyDefault(
  () => import('./routes/Admin/Inventory/IssuancesRetirementsPage')
);
const RetirementsPage = lazyDefault(
  () => import('./routes/Admin/Inventory/RetirementsPage')
);
const RetirementDetailPage = lazyDefault(
  () => import('./routes/Admin/Inventory/RetirementDetailPage')
);
const RetirementCertificateQueuePage = lazyDefault(
  () => import('./routes/Admin/Inventory/RetirementCertificateQueuePage')
);
const RetirementCreatePage = lazyDefault(
  () => import('./routes/Admin/Inventory/RetirementCreatePage')
);
const IssuanceDetailPage = lazyDefault(
  () => import('./routes/Admin/Inventory/IssuanceDetailPage')
);
const ContractDetailPage = lazyDefault(
  () => import('./routes/Admin/Inventory/ContractDetailPage')
);
const UserDiagnosticsPage = lazyDefault(
  () => import('./routes/Admin/UserDiagnostics/UserDiagnosticsPage')
);
const AdminChrome = lazyDefault(() => import('./routes/Admin/AdminChrome'));

export const PathHistoryContext = createContext<string[]>([]);

function AccountsOnly({ children }: PropsWithChildren<unknown>) {
  const user = useSelector((state: State) => state.user);
  const history = useHistory();
  const { location } = history;
  if (user.hasPassword) {
    return <>{children}</>;
  }
  history.replace(
    `/login?forward=${encodeURIComponent(
      location.pathname + location.search + location.hash
    )}`
  );
  return null;
}

function TeamRequired({ children }: PropsWithChildren<unknown>) {
  const user = useSelector((state: State) => state.user);
  if (
    user.teamMemberships?.length > 0 ||
    user.team ||
    (user.ownedTeams && user.ownedTeams.length > 0)
  ) {
    return <>{children}</>;
  }
  return <Redirect to="/dashboard" />;
}

function NonOnboardedUsersOnly({ children }: PropsWithChildren<unknown>) {
  const user = useSelector((state: State) => state.user);
  const history = useHistory();
  if (user.completedOnboarding) {
    history.replace('/dashboard');
    return null;
  }
  return <>{children}</>;
}

// Klima-imported users who haven't activated yet belong on the migration
// welcome page (which preserves their transferred amount/billing date), not the
// standard plan picker. They can reach /choose-plan via email links, the
// calculator, or /choose-project; send them to /klima instead — but only when
// /klima will actually render for them (it requires a subscription cart item),
// otherwise leave them on the plan picker.
function KlimaMigrationRedirect({ children }: PropsWithChildren<unknown>) {
  const user = useSelector((state: State) => state.user);
  const history = useHistory();
  const hasSubscriptionCartItem = !!user.cart?.items?.some(
    (item) => item.sku === 'subscription'
  );
  if (
    user.importedFromKlima &&
    !user.completedOnboarding &&
    hasSubscriptionCartItem
  ) {
    history.replace('/klima');
    return null;
  }
  return <>{children}</>;
}

function TeamAdminOnly({ children }: PropsWithChildren<unknown>) {
  const isTeamAdmin = useSelector(selectIsTeamAdmin);
  if (!isTeamAdmin) return null;
  return <>{children}</>;
}

function EmissionsBoundary({ children }: PropsWithChildren<unknown>) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorMessage error={error} resetError={resetError} />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

function PageContent() {
  const user = useSelector((state: State) => state.user);

  const dispatch = useDispatch();
  const location = useLocation();
  const routeHasNoNav = useRouteMatch(NavConfig.routesWithNoNav as string[]);
  // V2 Track 1: team-context routes get the new B2B DashboardLayout chrome
  // (its own sidebar + topbar) and omit the public marketing nav.
  const isTeamDashboardRoute =
    location.pathname.startsWith('/dashboard/team') ||
    location.pathname.startsWith('/dashboard/emissions') ||
    // /settings/team/* and /settings/shopify/* mount inside
    // TeamDashboardLayout (PageContent.tsx routes below), so the public
    // marketing <Nav /> needs to stay hidden here too — otherwise users
    // see two stacked navs on Settings.
    location.pathname.startsWith('/settings/team') ||
    location.pathname.startsWith('/settings/shopify');
  const showTeamDashboardSkeleton =
    isTeamDashboardRoute ||
    location.pathname === '/teams/manage' ||
    /^\/teams\/[^/]+\/manage(?:\/|$)/.test(location.pathname);
  const hideNav = routeHasNoNav?.isExact || isTeamDashboardRoute;
  const pathHistory = usePathHistory();
  useLastNonCheckoutPage(); // Track last non-checkout page for checkout redirects
  const { getQueryParam } = useQueryParams();

  const specialOfferToken = getQueryParam('so');
  const toastFromUrl = getQueryParam('toast');

  const pagesWithoutSubscriptionChurnNotice = [
    '/settings',
    '/success',
    '/update-payment',
    '/pwyw',
    '/goodbye!',
    '/checkout/payment',
  ];

  useEffect(() => {
    if (specialOfferToken) {
      dispatch(ClaimSpecialOffer(specialOfferToken));
    }
  }, [specialOfferToken]);

  useEffect(() => {
    if (toastFromUrl) {
      dispatch(SetToast({ text: toastFromUrl }));
    }
  }, [toastFromUrl]);

  useEffect(() => {
    if (user && shouldAutoSetCurrencyFromCountry(user) && !user.currency) {
      const currencyCode = guessCurrency(user.country || 'US');
      dispatch(UpdateCurrency(currencyCode));
    }
  }, [user]);

  const hasDelinquentPayment = isDelinquent(user.status);

  const flow = useOnboardingFlow();

  const isOnboardingRoute =
    (location.pathname !== '/' &&
      flow.getOnboardingSteps().includes(location.pathname as Step)) ||
    location.pathname.includes('/intro'); // helps exclude all intro questions

  const canShowBanner =
    location.pathname !== '/groups/join' &&
    (user.groups.length === 0 ||
      (!isOnboardingRoute && user.groups.length > 0)) &&
    !user.completedOnboarding;

  // The "Tomorrow's Air is now Wren Travel" banner is specific to the travel
  // offering; the business landing no longer shows it.
  const showTomorrowsAirBanner = location.pathname === '/travel';
  const showBusinessLandingSkeleton = [
    '/business',
    '/teams',
    '/teams/business',
  ].includes(location.pathname);
  const businessLandingLoaded = useBusinessLandingTransition(
    showBusinessLandingSkeleton
  );

  // const showWrenForBusinessBanner =
  //   location.pathname === '/' && user.specialOfferClaims.length === 0;

  return (
    <PathHistoryContext.Provider value={pathHistory}>
      <div css={{ position: 'inherit', minHeight: '100vh' }}>
        <HashLinkObserver />
        <ReferralDialog />
        <AdminModeControls />
        {hasDelinquentPayment &&
          !pagesWithoutSubscriptionChurnNotice.some((page) =>
            location.pathname.startsWith(page)
          ) && <SubscriptionChurnNotice />}
        {showTomorrowsAirBanner && <TomorrowsAirBanner />}
        {canShowBanner && <SpecialOffers user={user} />}
        {isOnboardingRoute &&
          user.groups.length > 0 &&
          // @ts-expect-error: translate to TS (group prop is optional)
          !user.completedOnboarding && <GroupCallout.Banner />}
        {!hideNav && <Nav />}
        {location.search.includes('bonus_type') && <BonusIncentive />}
        {showBusinessLandingSkeleton && !businessLandingLoaded && (
          <div className="absolute inset-x-0 top-0" aria-hidden="true">
            <BusinessLandingSkeleton />
          </div>
        )}
        <Suspense
          fallback={
            showBusinessLandingSkeleton ? null : showTeamDashboardSkeleton ? (
              <DashboardSkeleton />
            ) : (
              <SuspenseFallback
                show
                delay={500}
                width={140}
                css={{ marginTop: 150 }}
              />
            )
          }
        >
          <Switch>
            {/* FRONTFACING ONE OFF */}
            <Route exact path="/fb" children={<FB />} />
            <Route exact path="/pwyw" children={<PayWhatYouWant />} />
            <Route exact path="/klima" children={<Klima />} />
            <Route exact path="/pay-it-forward" children={<PayItForward />} />
            <Route
              exact
              path="/pif-success"
              children={<PayItForwardSuccess />}
            />
            <Route
              exact
              path="/join/:referralUsername"
              children={<ReferralLanding />}
            />
            {/* <Route
              exact
              path="/community-challenge/plant-based-week-2024"
              children={<CommunityChallenge />}
            /> */}
            <Route path="/wren-api" children={<WrenAPI />} />
            <Route
              exact
              path="/projects-summary"
              children={<ProjectsSummary />}
            />
            <Redirect exact path="/how-it-works" to="/our-approach" />
            <Route exact path="/charter" children={<UpdatedCharter />} />
            <Route exact path="/about-us" children={<AboutUs />} />
            <Route exact path="/offsets" children={<AboutOffsets />} />
            <Route exact path="/privacy-policy" children={<PrivacyPolicy />} />
            <Route
              exact
              path="/app-privacy"
              children={<ShopifyPrivacyPolicy />}
            />
            <Redirect exact from="/shopify-privacy" to="/app-privacy" />
            <Route exact path="/app-help" children={<ShopifyHelp />} />
            <Redirect exact from="/shopify-help" to="/app-help" />
            <Route
              exact
              path="/extension-privacy"
              children={<ExtensionPrivacyPolicy />}
            />
            <Route exact path="/terms-of-use" children={<TermsOfUse />} />
            <Route path="/certification" children={<Certification />} />
            <Redirect from="/about" to="/about-us" />
            <Route exact path="/faq" children={<FAQ />} />
            <Route exact path="/offsets" children={<OffsetOrders />} />
            <Route exact path="/impact" children={<OffsetOrders />} />
            <Route exact path="/leaderboard" children={<Leaderboards />} />
            <Route exact path="/our-approach" children={<OurApproach />} />
            <Route exact path="/giving-tuesday">
              <Redirect
                to={`/fundraisers/3c2d37bf-de29-405c-94aa-f156c0437d7b`}
              />
            </Route>
            <Route exact path="/give">
              <Redirect to={`/giving-tuesday`} />
            </Route>
            {/* FRONTFACING WITH SUBPAGES */}
            <Route exact path="/careers" children={<Careers />} />
            <Route exact path="/careers/:link" children={<JobPost />} />
            <Route path="/projects/wren-climate-fund">
              <Redirect to="/portfolios/universal-fund" />
            </Route>
            <Route path="/projects/regenerative-agroforestry">
              <Redirect to="/projects/enhanced-mineral-weathering" />
            </Route>
            <Route path="/projects/weforest">
              <Redirect to="/blog/tree-growing" />
            </Route>
            <Route path="/projects/mineral-weathering-in-scotland">
              <Redirect to="/projects/enhanced-mineral-weathering" />
            </Route>
            <Route path="/projects/biochar-in-california">
              <Redirect to="/projects/biochar-carbon-removal" />
            </Route>
            <Route path="/projects/:slug" children={<ProjectPage />} />
            <Route path="/projects/recoolit">
              <Redirect to="/projects/refrigerant-destruction" />
            </Route>
            <Route exact path="/projects" children={<OurProjects />} />
            <Route path="/trailblazer">
              <Redirect to="/portfolios/trailblazer" />
            </Route>
            <Route path="/biochar">
              <Redirect to="/blog/pacific-biochar-investment" />
            </Route>
            <Route path="/slingshot">
              <Redirect to="/fundraisers/6e9c14fc-8b86-4835-81e5-0e8720fea904" />
            </Route>
            <Route path="/classic">
              <Redirect to="/portfolios/universal-fund" />
            </Route>
            <Route path="/portfolios/classic">
              <Redirect to="/portfolios/universal-fund" />
            </Route>
            <Route path="/portfolios/:slug" children={<PortfolioPage />} />
            <Route
              path="/business-offsets"
              children={<OffsetAnything forBusiness />}
            />
            <Route path="/offset-anything" children={<OffsetAnything />} />
            <Route path="/crypto" children={<Crypto />} />
            {/* GROUPS */}
            <Route path="/groups/accept" children={<GroupAccept />} />
            <Route path="/groups/join" children={<GroupInvite />} />
            <Route path="/groups/:slug" children={<Group />} />
            <Route path="/groups" children={<GroupsLanding />} />
            {/* BLOG POSTS */}
            <Route
              path="/updates/:slug"
              render={({ match }) => (
                <Redirect to={`/blog/${match.params.slug ?? ''}`} />
              )}
            />
            <Route path="/updates">
              <Redirect to="/blog" />
            </Route>
            <Route exact path="/blog" children={<AllBlogPosts />} />
            <Route
              exact
              path="/blog/tag/:slug"
              children={<AllBlogPosts mode="tag" />}
            />
            <Route
              exact
              path="/blog/author/:slug"
              children={<AllBlogPosts mode="author" />}
            />
            <Route
              path="/blog/posts/:slug?"
              render={({ match }) => (
                <Redirect to={`/blog/${match.params.slug ?? ''}`} />
              )}
            />
            <Route
              path="/blog/:slug"
              render={({ match }) => (
                <ContentfulBlogPost slug={match.params.slug ?? ''} />
              )}
            />
            {/* ONBOARDING */}
            <Route path="/calculator/submit">
              <Redirect to="/choose-plan" />
            </Route>
            <Route path="/calculator" children={<Calculator />} />
            <Route
              path="/emissions/:step?/:subStep?"
              children={
                <EmissionsBoundary>
                  <EmissionsAgenticWizard />
                </EmissionsBoundary>
              }
            />
            <Route path="/choose-project">
              <Redirect to="/choose-plan" />
            </Route>
            <Route
              exact
              path="/choose-plan"
              children={
                <NonOnboardedUsersOnly>
                  <KlimaMigrationRedirect>
                    <ReportAndChoosePlan />
                  </KlimaMigrationRedirect>
                </NonOnboardedUsersOnly>
              }
            />
            <Route exact path="/continue" children={<ContinueOnboarding />} />
            <Route path="/checkout" children={<Checkout />} />
            <Route path="/intro/:questionSlug" children={<IntroSurvey />} />
            {/* ACCOUNT */}
            <Route exact path="/invite">
              <Redirect to="/dashboard/reach" />
            </Route>
            <Route path="/start/:token" children={<SpecialOfferLanding />} />
            <Route path="/profile/:username" children={<Profile />} />
            {/* For specific open graph images */}
            <Route
              path="/profile/:username/og/:opengraph"
              children={<Profile />}
            />
            {/* ACTIONS */}
            {/* moved canonically */}
            {/* redirect not-logged-in users away from dashboard/actions for convenience/sharing */}
            {!user.hasPassword && (
              <Route path="/dashboard/actions">
                <Redirect to="/actions" />
              </Route>
            )}
            <Route path="/actions" children={<Actions />} />
            {/* Old dashboard links */}
            <Route
              path="/dashboard/settings/:anything?"
              render={({ match }) => (
                <Redirect to={`/settings/${match.params.anything ?? ''}`} />
              )}
            />
            <Route exact path="/dashboard/funding/past-emissions">
              <Redirect to="/dashboard" /* to timeline */ />
            </Route>
            <Route exact path="/dashboard/funding/historical">
              <Redirect to="/dashboard" /* to timeline */ />
            </Route>
            <Route exact path="/dashboard/offsets/past-emissions">
              <Redirect to="/dashboard" /* to timeline */ />
            </Route>
            <Route
              path="/dashboard/offsets/:anything?"
              render={({ match }) => (
                <Redirect
                  to={`/dashboard/funding/${match.params.anything ?? ''}`}
                />
              )}
            />
            <Route
              path="/dashboard/invite/:anything?"
              render={({ match }) => (
                <Redirect to={`/settings/${match.params.anything ?? ''}`} />
              )}
            />
            <Route
              path="/dashboard/team"
              children={
                <AccountsOnly>
                  <TeamRequired>
                    <TeamDashboardLayout>
                      <Switch>
                        {/* ADR 0014 Phase 4: universal team-mode IA. The
                          `/dashboard/team` root redirects to /home so the
                          sidebar's Home entry stays the canonical landing
                          for the new chrome. The legacy TeamsDashboard
                          surface stays mounted at /dashboard/team/legacy
                          while the rich Home page ships. */}
                        {/* ADR 0014 Phase 5 — 90-day redirects from the
                          live-product URL shapes that Customer.io email
                          campaigns and external bookmarks may still
                          reference. Audited 2026-05-07: no active
                          campaign templates contain literal
                          /dashboard/team/employee-benefit or
                          /dashboard/team/api-and-credits URLs (search
                          hits were all body-copy mentions of the words),
                          so the redirects are pure safety net for
                          bookmarks. Drop after 2026-08-07. */}
                        <Route
                          path="/dashboard/team/employee-benefit"
                          children={<Redirect to="/dashboard/team/employees" />}
                        />
                        <Route
                          path="/dashboard/team/api-and-credits"
                          children={<Redirect to="/dashboard/team/api" />}
                        />
                        <Route
                          path="/dashboard/team/home"
                          children={<TeamHome />}
                        />
                        <Route
                          path="/dashboard/team/emissions"
                          children={
                            <TeamAdminOnly>
                              <EmissionsBoundary>
                                <Emissions />
                              </EmissionsBoundary>
                            </TeamAdminOnly>
                          }
                        />
                        <Route
                          path="/dashboard/team/reductions"
                          children={<TeamReductions />}
                        />
                        <Route
                          path="/dashboard/team/impact"
                          children={<TeamCompensate />}
                        />
                        <Route
                          path="/dashboard/team/comms"
                          children={<TeamCommsToolkit />}
                        />
                        <Route
                          path="/dashboard/team/api"
                          children={<TeamApi />}
                        />
                        <Route
                          exact
                          path="/dashboard/team/employees/setup"
                          children={<TeamEmployeeBenefitSetup />}
                        />
                        <Route
                          path="/dashboard/team/employees"
                          children={<TeamEmployees />}
                        />
                        <Route
                          path="/dashboard/team/shopify"
                          children={<TeamShopify />}
                        />
                        <Route
                          path="/dashboard/team/certificates"
                          children={<TeamCertificates />}
                        />
                        <Route
                          path="/dashboard/team/legacy"
                          children={<TeamsDashboard />}
                        />
                        {/* Bare /dashboard/team — match exact and redirect to home. */}
                        <Route
                          exact
                          path="/dashboard/team"
                          children={<Redirect to="/dashboard/team/home" />}
                        />
                        {/* Anything else under /dashboard/team falls through
                          to the legacy dashboard so old deep links don't
                          404 during the transition. */}
                        <Route
                          path="/dashboard/team"
                          children={<TeamsDashboard />}
                        />
                      </Switch>
                    </TeamDashboardLayout>
                  </TeamRequired>
                </AccountsOnly>
              }
            />
            <Route
              path="/dashboard/emissions"
              children={
                <AccountsOnly>
                  <TeamAdminOnly>
                    <EmissionsBoundary>
                      <TeamDashboardLayout>
                        <Emissions />
                      </TeamDashboardLayout>
                    </EmissionsBoundary>
                  </TeamAdminOnly>
                </AccountsOnly>
              }
            />
            <Route
              path="/dashboard"
              children={
                <AccountsOnly>
                  <Dashboard />
                </AccountsOnly>
              }
            />
            <Route exact path="/delete" children={<DeleteAccountPage />} />
            <Route
              path="/settings/trailblazer"
              children={
                <AccountsOnly>
                  <SwitchToTrailblazer />
                </AccountsOnly>
              }
            />
            {/* ADR 0014 Phase 4 — keep the team-mode chrome around the
                Settings tree so the sidebar persists when a customer
                clicks Settings from /dashboard/team/*. The full move to
                /dashboard/team/settings/* lands when the new chrome
                ships; in the meantime the existing /settings/team URLs
                stay canonical and just gain the wrapper. */}
            <Route
              path="/settings/team"
              children={
                <AccountsOnly>
                  <TeamRequired>
                    <TeamDashboardLayout>
                      <AccountSettings />
                    </TeamDashboardLayout>
                  </TeamRequired>
                </AccountsOnly>
              }
            />
            <Route
              path="/settings/shopify"
              children={
                <AccountsOnly>
                  <TeamRequired>
                    <TeamDashboardLayout>
                      <AccountSettings />
                    </TeamDashboardLayout>
                  </TeamRequired>
                </AccountsOnly>
              }
            />
            <Route
              path="/settings"
              children={
                <AccountsOnly>
                  <AccountSettings />
                </AccountsOnly>
              }
            />
            <Route path="/subscription">
              {/* For convenience and short sms urls */}
              <Redirect to="/settings/subscription" />
            </Route>
            <Route
              exact
              path="/public-footprint/:username"
              children={<PublicFootprint />}
            />
            <Route
              exact
              path="/footprint/:username"
              children={<PublicFootprint />}
            />
            <Route exact path="/login" children={<Login />} />
            <Route exact path="/sign-up" children={<SignUp />} />
            <Route exact path="/logout" children={<LogOut />} />
            <Route
              exact
              path="/forgot-password"
              children={<ForgotPassword />}
            />
            <Route exact path="/reset" children={<RequestPassReset />} />
            <Route exact path="/confirm-email" children={<ConfirmEmail />} />
            <Route exact path="/update-payment" children={<UpdatePayment />} />
            <Route
              exact
              path="/update-team-payment"
              children={<UpdateTeamPaymentMethod />}
            />
            {/* FLIGHTS */}
            <Route path="/flights" children={<Flights />} />
            {/* FLIGHTS */}
            <Route path="/travel" children={<TravelLanding />} />
            {/* TEAMS */}
            <Route path="/teams" children={<Teams />} />
            <Route path="/benefit">
              <Redirect to="/teams" />
            </Route>
            <Route
              path="/business/jameo-case-study"
              children={<JameoCaseStudy />}
            />
            <Route
              path="/business/eavolu-case-study"
              children={<EavoluCaseStudy />}
            />
            <Route path="/business">
              <Redirect to="/teams/business" />
            </Route>
            {/* GIFTS */}
            <Route path="/gifts" children={<Gifts />} />
            <Route path="/links" children={<LinkInBio />} />
            <Route path="/admin">
              {!user.hasPassword ? (
                <Redirect
                  to={`/login?forward=${encodeURIComponent(location.pathname)}`}
                />
              ) : !user.isAdmin ? (
                <Private />
              ) : (
                <AdminChrome>
                  <Switch>
                    <Redirect exact from="/admin" to="/admin/inventory" />
                    <Route
                      exact
                      path="/admin/portfolio-creator"
                      children={<PortfolioCreator />}
                    />
                    <Route
                      exact
                      path="/admin/projects/new"
                      children={<ProjectCreator />}
                    />
                    <Route
                      exact
                      path="/admin/projects/:projectId/edit"
                      children={<ProjectEditor />}
                    />
                    <Route
                      exact
                      path="/admin/projects/:projectId"
                      children={<ProjectDetailCard />}
                    />
                    <Route
                      exact
                      path="/admin/projects"
                      children={<ProjectsList />}
                    />
                    <Route
                      exact
                      path="/admin/manual-offsets"
                      children={<ManualOffsetCreator />}
                    />
                    <Route
                      exact
                      path="/admin/reservations"
                      children={<Reservations />}
                    />
                    <Route
                      exact
                      path="/admin/teams"
                      children={<AdminTeamsDashboard />}
                    />
                    <Route
                      path="/admin/teams/:teamId"
                      children={<TeamDetailPage />}
                    />
                    <Route
                      exact
                      path="/admin/link-in-bio"
                      children={<LinkInBioDashboard />}
                    />
                    <Route
                      exact
                      path="/admin/team-creator"
                      children={<TeamCreator />}
                    />
                    <Route
                      exact
                      path="/admin/documents"
                      children={<Documents />}
                    />
                    <Route
                      exact
                      path="/admin/payments/redactions"
                      children={<RedactionQueuePage />}
                    />
                    <Route
                      path="/admin/payments/:paymentId"
                      children={<PaymentDetailPage />}
                    />
                    <Route
                      exact
                      path="/admin/payments"
                      children={<PaymentsPage />}
                    />
                    <Route
                      path="/admin/contracts/:contractId"
                      children={<ContractDetailPage />}
                    />
                    <Route
                      path="/admin/issuances/:issuanceId"
                      children={<IssuanceDetailPage />}
                    />
                    <Route
                      exact
                      path="/admin/issuances"
                      children={<IssuancesRetirementsPage />}
                    />
                    <Route
                      exact
                      path="/admin/retirements"
                      children={<RetirementsPage />}
                    />
                    <Route
                      exact
                      path="/admin/retirements/new"
                      children={<RetirementCreatePage />}
                    />
                    <Route
                      exact
                      path="/admin/retirements/redactions"
                      children={<RetirementCertificateQueuePage />}
                    />
                    <Route
                      path="/admin/retirements/:retirementId"
                      children={<RetirementDetailPage />}
                    />
                    <Redirect
                      exact
                      from="/admin/issuances-retirements"
                      to="/admin/issuances"
                    />
                    <Route
                      exact
                      path="/admin/inventory/actions"
                      children={<ActionCenter />}
                    />
                    <Route
                      path="/admin/inventory/:projectId"
                      children={<ProjectDetail />}
                    />
                    <Route
                      exact
                      path="/admin/inventory"
                      children={<InventoryDashboard />}
                    />
                    <Route
                      exact
                      path="/admin/revenue"
                      children={<RevenueDashboard />}
                    />
                    <Route
                      exact
                      path="/admin/forecast"
                      children={<ForecastDashboard />}
                    />
                    <Route
                      exact
                      path="/admin/help"
                      children={<InventoryHelpPage />}
                    />
                    <Route
                      exact
                      path="/admin/user-diagnostics"
                      children={<UserDiagnosticsPage />}
                    />
                    <Route
                      path="/admin/user-diagnostics/:userId"
                      children={<UserDiagnosticsPage />}
                    />
                    <Route>
                      <Redirect to="/admin/inventory" />
                    </Route>
                  </Switch>
                </AdminChrome>
              )}
            </Route>
            <Route path="/give-wren">
              <Redirect to="/gifts" />
            </Route>
            <Route
              path="/gift/:giftId"
              render={({ match }) => (
                <Redirect to={`/gifts/receive/${match.params.giftId}`} />
              )}
            />
            {/* SOME MORE PROTECTED ROUTES */}
            <Route
              exact
              path="/success"
              children={
                <AccountsOnly>
                  <SubscriptionSuccess />
                </AccountsOnly>
              }
            />
            <Route
              exact
              path="/upgrade"
              children={
                <AccountsOnly>
                  <UpgradeSubscription />
                </AccountsOnly>
              }
            />
            <Route
              exact
              path="/fundraisers/new"
              children={<FundraisersNew />}
            />
            <Route exact path="/fundraisers" children={<FundraisersIndex />} />
            <Route
              exact
              path="/fundraisers/:id"
              children={<FundraiserPage />}
            />
            <Route exact path="/receipt/:id" children={<DonationReceipt />} />
            <Route
              exact
              path="/certificate/:id"
              children={<ImpactCertificate />}
            />
            <Route
              exact
              path="/purchase/by-group/:id"
              children={<PurchasePage />}
            />
            <Route exact path="/purchase/:id" children={<PurchasePage />} />
            <Route exact path="/retirement/:id" children={<RetirementPage />} />
            <Route
              exact
              path="/fundraisers/:id/edit"
              children={<FundraisersEdit />}
            />
            <Route
              exact
              path="/fundraisers/:id/success"
              children={<FundraiserSuccess />}
            />
            <Route
              exact
              path="/fundraisers/:id/donate"
              children={<FundraiserDonatePage />}
            />
            {/* MISC */}
            <Route exact path="/private" children={<Private />} />
            <Route exact path="/goodbye!" children={<CancellationMessage />} />
            <Route path="/emails" children={<Emails />} />
            <Route
              path="/donate/:portfolioSlug/success"
              exact
              children={
                <AccountsOnly>
                  <DonateSuccess />
                </AccountsOnly>
              }
            ></Route>
            <Route
              path="/donate/:portfolioSlug"
              exact
              children={<Donate />}
            ></Route>
            <Route path="/email">
              <Redirect to="/" />
            </Route>
            <Route path="/youtube">
              <Redirect to="/" />
            </Route>
            {/* One-off holiday thing */}
            <Route path="/waigifts">
              <Redirect to="/gifts" />
            </Route>
            <Route path="/shop/profile/:username">
              <ShopifyPublicProfile />
            </Route>
            <Route
              exact
              path="/shopify/:username"
              render={({ match }) => (
                <Redirect to={`/shop/profile/${match.params.username}`} />
              )}
            />
            <Route path="/shop/choose-account">
              <ShopifyChooseAccount />
            </Route>
            <Route path="/shop">
              <ShopifyOnboarding />
            </Route>
            <Route path="/reductions">
              <Redirect to="/actions" />
            </Route>
            <Route path="/subscription">
              <Redirect to="/continue" />
            </Route>
            <Route path="/reports">
              <Redirect to="/blog" />
            </Route>
            <Route path="/philanthropy">
              <Redirect to="/collective" />
            </Route>
            <Route path="/sfcw">
              <Redirect to="/fundraisers/68b4d0de-3ce4-4e76-bac8-c3bc6a764eb2" />
            </Route>
            <Route path="/collective/donate" exact children={<HNWDonate />} />
            <Route path="/collective" children={<HNWLandingPage />} />
            <Route path="/birdseye" children={<BirdseyeLanding />} />
            {/* NOT FOUND: */}
            <Route exact path="/404" children={<NotFound />} />
            <Route children={<NotFound />} />
          </Switch>
        </Suspense>
        <Toaster />
        <Global styles={GlobalStyles} />
      </div>
    </PathHistoryContext.Provider>
  );
}

export default PageContent;
